

















import { Component, Watch } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import { namespace } from "vuex-class";
import { Auth } from "@/mixins/auth";
import { Roles } from "@/models/user/roles";
import AuthService from "@/services/auth_service";
import NetSuiteService from "@/services/netsuite_service";
import Header from "@/components/header/Header.vue";
import Sidebar from "@/components/sidebar/Menu.vue";
import CartModal from "@/components/elements/modal/CartModal.vue";
import ImpersonationBanner from "@/components/elements/ImpersonationBanner.vue";
import ToastNotifications from "@/components/elements/toast/ToastNotifications.vue";
import { EventBus } from "./event-bus";
import { Mobile } from "@/mixins/mobile";
import { LineItem } from "./models/order/order";
import { User } from "./models/user/user";
import { OrderMixin } from "./mixins/orders/mixin";
import { PartialNetSuiteCustomer, PriceLevel } from "./models/netsuite/netsuite";
import { GlobalActions, GlobalGetters } from "./store/modules/global/types";
import { StoreModule } from "./store/types";

@Component({
  components: {
    ImpersonationBanner,
    Header,
    Sidebar,
    CartModal,
    ToastNotifications
  }
})
export default class App extends mixins(Auth, Mobile, OrderMixin) {
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetPriceLevels))
  priceLevels!: PriceLevel[];

  @(namespace(StoreModule.Global).Action(GlobalActions.AddPriceLevels))
  addPriceLevels!: (value: PriceLevel[]) => Promise<void>;

  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetCustomers))
  storeCustomers!: PartialNetSuiteCustomer[];

  @(namespace(StoreModule.Global).Action(GlobalActions.AddCustomers))
  addCustomers!: (value: PartialNetSuiteCustomer[]) => Promise<void>;

  private surchargeValue = process.env.VUE_APP_PRICE_SURCHARGE_VALUE_NUMBER || "";
  private surchargeDate = process.env.VUE_APP_PRICE_SURCHARGE_DATE || "";
  private surchargeDismissed = sessionStorage.getItem("surcharge_banner_dismissed") === "true";

  get hasSurchargeBanner(): boolean {
    return !this.surchargeDismissed && !!this.surchargeValue && !!this.surchargeDate;
  }

  protected environment = process.env.VUE_APP_ENV;
  protected envBuildBranch = process.env.VUE_APP_BUILD_BRANCH;
  protected envBuildDate = process.env.VUE_APP_BUILD_DATE;
  protected Roles = Roles;
  protected authService = new AuthService();
  protected netSuiteService = new NetSuiteService();
  protected showModal = false;
  protected loader: any = null;
  protected cartInfo: {
    qty?: number;
    model: string;
    productline?: string;
    name?: string;
    finish?: string;
    fabric?: string;
    cost?: number;
    quickshipList?: LineItem[];
    accessoryList?: LineItem[];
    sku?: string;
  } | null = null;

  async created(): Promise<void> {
    if (this.environment === "development") {
      console.log(
        `Logged Dev App Build Details: Branch - ${
          this.envBuildBranch ? this.envBuildBranch : "N/A"
        } | Date - ${this.envBuildDate ? this.envBuildDate : "N/A"}`
      );
    }
    this.checkAuth();
    EventBus.$on("ssoAuthComplete", () => {
      this.checkAuth();
    });
    EventBus.$on("surchargeBannerDismissed", () => {
      this.surchargeDismissed = true;
    });
    EventBus.$on(
      "addToCartModal",
      (cartInfo: {
        qty?: number;
        model: string;
        productline?: string;
        name?: string;
        finish?: string;
        fabric?: string;
        options?: object;
        cost?: number;
        quickshipList?: LineItem[];
        accessoryList?: LineItem[];
        sku?: string;
      }) => {
        this.showModal = true;
        this.cartInfo = cartInfo;
      }
    );

    this.handleView();
    window.addEventListener("resize", this.handleView);
  }

  protected handleView() {
    this.setMobile(window.innerWidth <= 640);
    this.setTablet(window.innerWidth < 960 && window.innerWidth > 640);
  }

  // protected closeModal(): void {
  //   this.showModal = false;
  //   this.editItem();
  // }

  protected viewCart(): void {
    this.showModal = false;
    this.$router.push("/cart");
  }

  protected async checkAuth(): Promise<void> {
    // Skip auth check on callback/login routes — AuthCallback handles token exchange
    const path = this.$route?.path;
    if (path === "/auth/callback" || path === "/login") {
      return;
    }

    // @ts-ignore — accessing store state directly for token
    const token = this.$store.state.global.authToken;
    if (!token) {
      this.setLoggedIn(false);
      return;
    }

    this.showLoader();
    try {
      const cachedUser = this.user;
      const role = this.userRole;
      const isAdmin = role === Roles.Admin;

      if (!isAdmin && !cachedUser?.netsuite_id) {
        this.setLoggedIn(false);
        this.setUserRole(null);
        this.setAuthToken(null);
        this.setUserInfo({} as User);
        await this.authService.logout();
        this.closeLoader();
        this.$router.push("/error");
        return;
      }

      if (!this.priceLevels?.length) {
        const priceLevels = await this.netSuiteService.getPriceLevels();
        await this.addPriceLevels(priceLevels);
      }

      this.setLoggedIn(true);

      if (!this.storeCustomers?.length) {
        const customers = (isAdmin)
          ? await this.netSuiteService.getPartialNetSuiteCustomers()
          : cachedUser?.netsuite_id
            ? await this.netSuiteService.getMyCustomers()
            : [];
        await this.addCustomers(customers);
      }
      this.closeLoader();
    } catch (err) {
      console.error("checkAuth error:", err);
      this.setLoggedIn(false);
      this.setUserRole(null);
      this.setAuthToken(null);
      this.setUserInfo({} as User);
      this.$router.push("/login");
    }
  }

  protected showLoader(): void {
    this.loader = this.$loading.show(
      {
        opacity: 1
      },
      {
        before: this.$createElement("h1", {
          domProps: { innerHTML: "Authenticating user..." }
        })
      }
    );
  }

  protected closeLoader(): void {
    setTimeout(() => {
      this.loader.hide();
    }, 300);
  }

  beforeDestroy(): void {
    EventBus.$off("addToCartModal");
    EventBus.$off("surchargeBannerDismissed");
  }
}
